export type UserRole = 'customer' | 'admin' | 'workshop';

export type AuthenticatedUser = {
  id: string;
  email: string;
  emailVerified: boolean;
  firstName: string;
  lastName: string;
  displayName: string;
  roles: UserRole[];
  createdAt?: string | undefined;
};

export type AuthSession = {
  user: AuthenticatedUser;
  expiresAt: string;
};

export type AuthStatus = 'initializing' | 'authenticated' | 'unauthenticated';

export type LoginInput = {
  email: string;
  password: string;
  rememberMe: boolean;
};

export type RegisterInput = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  acceptTerms: boolean;
  marketingOptIn: boolean;
};

export type VerifyEmailInput = { email: string; code: string };
export type ResendVerificationCodeInput = { email: string };
export type ForgotPasswordInput = { email: string };
export type ResetPasswordInput = { email: string; code: string; newPassword: string };

export type RegistrationResult = {
  email: string;
  verificationRequired: boolean;
};

export type AuthErrorCode =
  | 'invalid-credentials'
  | 'email-not-verified'
  | 'email-already-exists'
  | 'invalid-verification-code'
  | 'expired-verification-code'
  | 'weak-password'
  | 'password-reset-required'
  | 'too-many-attempts'
  | 'network-error'
  | 'session-expired'
  | 'unauthorized'
  | 'unsupported-auth-challenge'
  | 'unknown';

export type AuthErrorField = 'email' | 'password' | 'code';

export class AuthError extends Error {
  readonly code: AuthErrorCode;
  readonly field: AuthErrorField | undefined;
  override readonly cause: unknown;

  constructor(options: {
    code: AuthErrorCode;
    message: string;
    field?: AuthErrorField;
    cause?: unknown;
  }) {
    super(options.message);
    this.name = 'AuthError';
    this.code = options.code;
    this.field = options.field;
    this.cause = options.cause;
  }
}

export type SupportedLocale = 'en-US' | 'tr-TR';

export type CommunicationPreferences = {
  transactionalEmail: true;
  productUpdatesEmail: boolean;
  promotionsEmail: boolean;
};

export type CustomerProfile = {
  userId: string;
  email: string;
  emailVerified: boolean;
  firstName: string;
  lastName: string;
  displayName: string;
  locale: SupportedLocale;
  communicationPreferences: CommunicationPreferences;
  profileStatus: 'active';
  version: number;
  createdAt: string;
  updatedAt: string;
};

export type ProfileUpdateInput = {
  firstName: string;
  lastName: string;
  locale: SupportedLocale;
  expectedVersion: number;
};

export type CommunicationPreferencesUpdateInput = {
  productUpdatesEmail: boolean;
  promotionsEmail: boolean;
  expectedVersion: number;
  policyVersion: string;
};

export type ConsentType = 'terms' | 'privacy' | 'marketing-email' | 'product-updates-email';
export type ConsentAction = 'granted' | 'withdrawn' | 'acknowledged';
export type ConsentSource = 'registration' | 'account-settings' | 'system-migration';

export type ConsentAuditRecord = {
  id: string;
  userId: string;
  consentType: ConsentType;
  action: ConsentAction;
  policyVersion: string;
  occurredAt: string;
  source: ConsentSource;
  requestId?: string | undefined;
};

export type ConsentInitializationInput = {
  termsPolicyVersion: string;
  privacyPolicyVersion: string;
  marketingEmail?: boolean | undefined;
  marketingPolicyVersion?: string | undefined;
};

export type ConsentHistoryPage = {
  items: ConsentAuditRecord[];
  nextCursor: string | null;
};

export type EmailChangeRequestInput = { newEmail: string };
export type EmailChangeConfirmationInput = { newEmail: string; code: string };
export type EmailChangeResult =
  { status: 'verification-required' } | { status: 'completed'; profile: CustomerProfile };

export type CustomerProfileResult = {
  profile: CustomerProfile;
  consentSetupRequired: boolean;
};

export type PolicyVersions = {
  terms: string;
  privacy: string;
  marketing: string;
};

export const currentPolicyVersions: PolicyVersions = {
  terms: policyVersions.terms,
  privacy: policyVersions.privacy,
  marketing: policyVersions.marketing,
};

export type ProductType = 'wood-engraving' | 'ornament' | 'tshirt';

export type CatalogStatus = 'draft' | 'published' | 'archived';
export type CategoryStatus = CatalogStatus;
export type ProductStatus = CatalogStatus;
export type Money = { amountMinor: number; currency: 'USD' };
export type ProductBadge = 'bestseller' | 'new' | 'popular' | 'seasonal';
export type SeoMetadata = { title?: string | undefined; description?: string | undefined };
export type ProductMediaKind = 'image';
export type ProductMediaRole = 'primary' | 'gallery' | 'thumbnail' | 'customization-preview';
export type ProductMediaReference = {
  id: string;
  kind: ProductMediaKind;
  role: ProductMediaRole;
  objectKey: string;
  altText: string;
  width?: number | undefined;
  height?: number | undefined;
  contentType?: string | undefined;
  displayOrder: number;
};
export type ProductOptionValue = {
  id: string;
  value: string;
  label: string;
  displayOrder: number;
  priceAdjustment?: Money | undefined;
  active: boolean;
};
export type ProductOptionDefinition = {
  id: string;
  name: string;
  label: string;
  required: boolean;
  displayOrder: number;
  values: ProductOptionValue[];
};
export type ProductVariant = {
  id: string;
  sku: string;
  optionValueIds: string[];
  priceOverride?: Money | undefined;
  active: boolean;
};
export type CustomizationTextInputDefinition = {
  id: string;
  label: string;
  required: boolean;
  maxLength: number;
  multiline: boolean;
  placeholder?: string | undefined;
};
export type CustomizationImageUploadDefinition = {
  required: boolean;
  acceptedContentTypes: string[];
  maxFiles: number;
  maxFileSizeBytes: number;
};
export type CustomizationDesignArea = {
  width: number;
  height: number;
  unit: 'px';
  safeAreaInset?: number | undefined;
};
export type ProductCustomizationConfiguration = {
  mode: 'none' | 'wood-engraving' | 'ornament' | 'tshirt-print';
  textInputs: CustomizationTextInputDefinition[];
  imageUpload?: CustomizationImageUploadDefinition | undefined;
  designArea?: CustomizationDesignArea | undefined;
  customerNotesAllowed: boolean;
};
export type ProductCategory = {
  id: string;
  slug: string;
  name: string;
  shortDescription: string;
  description: string;
  status: CategoryStatus;
  displayOrder: number;
  heroMedia?: ProductMediaReference | undefined;
  seo: SeoMetadata;
  version: number;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string | undefined;
  archivedAt?: string | undefined;
};
export type CatalogProduct = {
  id: string;
  slug: string;
  sku: string;
  categoryId: string;
  productType: ProductType;
  name: string;
  shortDescription: string;
  description: string;
  status: ProductStatus;
  basePrice: Money;
  compareAtPrice?: Money | undefined;
  badges: ProductBadge[];
  featured: boolean;
  featuredOrder?: number | undefined;
  merchandisingOrder: number;
  customizable: boolean;
  customization: ProductCustomizationConfiguration;
  options: ProductOptionDefinition[];
  variants: ProductVariant[];
  media: ProductMediaReference[];
  seo: SeoMetadata;
  version: number;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string | undefined;
  archivedAt?: string | undefined;
};
export type CreateCategoryInput = Omit<
  ProductCategory,
  'id' | 'status' | 'version' | 'createdAt' | 'updatedAt' | 'publishedAt' | 'archivedAt' | 'slug'
> & { slug?: string | undefined };
export type UpdateCategoryInput = Omit<CreateCategoryInput, 'slug'> & {
  slug: string;
  expectedVersion: number;
};
export type CreateProductInput = Omit<
  CatalogProduct,
  'id' | 'status' | 'version' | 'createdAt' | 'updatedAt' | 'publishedAt' | 'archivedAt' | 'slug'
> & { slug?: string | undefined };
export type UpdateProductInput = Omit<CreateProductInput, 'slug'> & {
  slug: string;
  expectedVersion: number;
};
export type CatalogLifecycleInput = { expectedVersion: number };
export type DuplicateProductInput = {
  name?: string | undefined;
  slug?: string | undefined;
  sku: string;
};
export type CatalogAuditAction =
  | 'category-created'
  | 'category-updated'
  | 'category-published'
  | 'category-archived'
  | 'product-created'
  | 'product-updated'
  | 'product-published'
  | 'product-archived'
  | 'product-duplicated';
export type CatalogAuditRecord = {
  id: string;
  entityType: 'category' | 'product';
  entityId: string;
  action: CatalogAuditAction;
  actorUserId: string;
  actorRoles: UserRole[];
  occurredAt: string;
  entityVersion: number;
  summary: Record<string, string | number | boolean | null>;
  requestId?: string | undefined;
};
export type PaginatedResult<T> = { items: T[]; nextCursor: string | null };
export type PublicationValidationIssue = {
  code: string;
  path?: string | undefined;
  message: string;
};
export type PublicCategorySummary = Pick<
  ProductCategory,
  'id' | 'slug' | 'name' | 'shortDescription' | 'displayOrder' | 'heroMedia' | 'seo'
>;
export type PublicProductSummary = {
  id: string;
  slug: string;
  categorySlug: string;
  productType: ProductType;
  name: string;
  shortDescription: string;
  startingPrice: Money;
  compareAtPrice?: Money | undefined;
  badges: ProductBadge[];
  featured: boolean;
  customizable: boolean;
  primaryMedia: ProductMediaReference;
};
export type PublicProductDetail = PublicProductSummary & {
  description: string;
  media: ProductMediaReference[];
  options: ProductOptionDefinition[];
  customization: ProductCustomizationConfiguration;
  seo: SeoMetadata;
};

export type CatalogMediaStatus =
  'initiated' | 'uploaded' | 'processing' | 'ready' | 'failed' | 'expired' | 'deleted';
export type CatalogMediaOwnerType = 'unassigned' | 'product' | 'category';
export type CatalogMediaVariantName = 'thumbnail' | 'card' | 'detail' | 'zoom' | 'original-display';
export type CatalogMediaVariant = {
  name: CatalogMediaVariantName;
  objectKey: string;
  width: number;
  height: number;
  contentType: 'image/webp';
  sizeBytes: number;
  etag?: string | undefined;
};
export type AdminCatalogMediaSummary = {
  id: string;
  status: CatalogMediaStatus;
  originalFileName: string;
  declaredContentType: 'image/jpeg' | 'image/png' | 'image/webp';
  detectedContentType?: 'image/jpeg' | 'image/png' | 'image/webp' | undefined;
  originalWidth?: number | undefined;
  originalHeight?: number | undefined;
  originalSizeBytes?: number | undefined;
  variants: CatalogMediaVariant[];
  ownerType: CatalogMediaOwnerType;
  ownerId?: string | undefined;
  altText?: string | undefined;
  failureCode?: CatalogMediaFailureCode | undefined;
  createdAt: string;
  uploadedAt?: string | undefined;
  processedAt?: string | undefined;
  expiresAt?: string | undefined;
  processingVersion: number;
  version: number;
};
export type CatalogMediaFailureCode =
  | 'unsupported-format'
  | 'content-type-mismatch'
  | 'file-too-large'
  | 'dimensions-too-small'
  | 'dimensions-too-large'
  | 'pixel-count-too-large'
  | 'corrupt-image'
  | 'animated-image-not-supported'
  | 'processing-timeout'
  | 'source-object-missing'
  | 'storage-error'
  | 'internal-processing-error';
export type InitiateCatalogMediaUploadInput = {
  fileName: string;
  contentType: 'image/jpeg' | 'image/png' | 'image/webp';
  sizeBytes: number;
};
export type CatalogMediaUploadInitiation = {
  mediaId: string;
  uploadUrl: string;
  method: 'POST';
  fields: Record<string, string>;
  expiresAt: string;
  maxSizeBytes: number;
};
export type AdminCatalogMediaListInput = {
  status?: CatalogMediaStatus | undefined;
  ownerType?: CatalogMediaOwnerType | undefined;
  ownerId?: string | undefined;
  exactMediaId?: string | undefined;
  sort?: 'newest' | 'oldest' | undefined;
  limit?: number | undefined;
  cursor?: string | undefined;
};
export type AttachCatalogMediaInput = {
  ownerType: 'product' | 'category';
  ownerId: string;
  role: ProductMediaRole;
  altText: string;
  displayOrder: number;
  expectedOwnerVersion: number;
};
export type DetachCatalogMediaInput = {
  expectedOwnerVersion: number;
};
export type CatalogMediaOwnerMutationResult = {
  media: AdminCatalogMediaSummary;
  product?: CatalogProduct | undefined;
  category?: ProductCategory | undefined;
};

export type OrderStatus =
  'draft' | 'pending-payment' | 'paid' | 'in-production' | 'shipped' | 'delivered' | 'cancelled';
import policyVersions from '../policy-versions.json';
